/**
 * Vigencia de la sesión del panel.
 *
 * El API entrega un JWT que vence a la hora (LoginAction) y, vencido, responde
 * 401 a todo. El panel solo miraba si había un token guardado: pasada la hora
 * la página seguía abierta y cada consulta fallaba sin explicación.
 */

import { getFromLocalStorage, saveToLocalStorage } from "./storage";

/** Evento que emite apiServices cuando el API rechaza el token. */
export const EVENTO_SESION_RECHAZADA = "sesion-rechazada";

/** Evento que emite apiServices cuando el API entrega un token renovado. */
export const EVENTO_SESION_RENOVADA = "sesion-renovada";

/** Cabecera en la que el API devuelve el token renovado (Sesion.php). */
export const CABECERA_TOKEN_RENOVADO = "X-Nuevo-Token";

/**
 * El API no reemite el token si tiene menos de 10 minutos (Sesion.php):
 * pedir la renovación antes sería una petición inútil.
 */
export const RENOVAR_DESPUES_DE_MS = 10 * 60 * 1000;

export const avisarSesionRechazada = () =>
  window.dispatchEvent(new Event(EVENTO_SESION_RECHAZADA));

/**
 * Guarda el token que el API renovó y avisa a AuthProvider para que
 * reprograme el cierre automático.
 *
 * Si ya no hay sesión guardada (se cerró mientras la petición viajaba) no se
 * hace nada: un token renovado no debe resucitar una sesión cerrada.
 */
export const guardarTokenRenovado = (jwt) => {
  const usuario = getFromLocalStorage("user");
  if (!jwt || !usuario?.jwt || usuario.jwt === jwt) return;

  saveToLocalStorage("user", { ...usuario, jwt });
  window.dispatchEvent(new Event(EVENTO_SESION_RENOVADA));
};

/** Datos del token (su parte central, JSON en base64url), o null. */
const datosDelToken = (jwt) => {
  try {
    const parte = String(jwt).split(".")[1];
    const base64 = parte.replace(/-/g, "+").replace(/_/g, "/");
    const relleno = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");

    return JSON.parse(atob(relleno));
  } catch (e) {
    return null;
  }
};

/**
 * Momento en que vence el token, en milisegundos, o null si no se puede leer.
 *
 * El JWT no va cifrado: su parte central es JSON en base64url y trae `exp`.
 * Leerlo aquí no sustituye la validación del API; solo permite adelantarse.
 */
export const vencimientoDelToken = (jwt) => {
  const exp = datosDelToken(jwt)?.exp;
  return typeof exp === "number" ? exp * 1000 : null;
};

/** Momento en que se emitió el token, en milisegundos, o null. */
export const emisionDelToken = (jwt) => {
  const iat = datosDelToken(jwt)?.iat;
  return typeof iat === "number" ? iat * 1000 : null;
};

/**
 * Si el token sigue vigente. Uno que no se puede leer se da por bueno: el API
 * es quien decide, y si lo rechaza el 401 cierra la sesión igual.
 */
export const tokenVigente = (jwt) => {
  if (!jwt) return false;

  const vence = vencimientoDelToken(jwt);
  return vence === null || vence > Date.now();
};
